/*
  CourseList.js
  Lista de cursos de e-Learning da Academia Ciber.
  Preços omitidos intencionalmente — visitante é direcionado para contato.
*/

// prop-types
import PropTypes from "prop-types";

// @mui material components
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import Divider from "@mui/material/Divider";

// Material Kit 2 PRO React components
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import MKButton from "components/MKButton";
import MKBadge from "components/MKBadge";

// Dados dos cursos
const elearningCourses = [
  {
    title: "Estrutura de Avaliação Cibernética (CAF)",
    badge: "NCSC Assured",
    duration: "30 minutos",
    audience: "Todos os níveis",
    description:
      "Introdução ao Cyber Assessment Framework do NCSC — voltado para organizações de Infraestrutura Nacional Crítica. Aborda objetivos, princípios e Indicadores de Boas Práticas, com aprendizado baseado em cenários reais.",
    highlights: [
      "Foco e aplicação do CAF",
      "Implementação e conformidade",
      "Indicadores de Boas Práticas",
      "Avaliação e certificado de conclusão",
    ],
  },
  {
    title: "Conscientização em Segurança Cibernética e Trabalho Remoto",
    badge: "NCSC Assured",
    duration: "45 minutos",
    audience: "Todos os colaboradores",
    description:
      "Eleva rapidamente o nível de conscientização de toda a empresa. Cobre desde tratamento de dados pessoais até melhores práticas para ambientes remotos e híbridos.",
    highlights: [
      "A importância da segurança cibernética",
      "Proteção de dados pessoais (LGPD)",
      "Boas práticas em trabalho remoto",
      "Avaliação e certificado de conclusão",
    ],
  },
  {
    title: "Diretiva de Segurança da Informação de Rede (NISD)",
    badge: "NCSC Assured",
    duration: "30 minutos",
    audience: "Operadores de serviços essenciais",
    description:
      "Conhecimentos essenciais sobre a Diretiva NIS da UE, que regula a segurança de infraestruturas críticas. O não cumprimento pode resultar em multas de até €17 milhões.",
    highlights: [
      "Âmbito e obrigações da legislação",
      "Funções e responsabilidades",
      "Gestão de incidentes e relatórios",
      "Construção de resiliência cibernética",
    ],
  },
  {
    title: "Segurança Cibernética para Proprietários de Dados e Gestores Seniores",
    badge: "NCSC Assured",
    duration: "40 minutos",
    audience: "C-level, diretores e gestores",
    description:
      "Curso focado nas responsabilidades de liderança em segurança da informação. Aborda gestão de riscos, governança, legislação aplicada e construção de uma cultura cibernética madura.",
    highlights: [
      "Cenário de ameaças e responsabilidades",
      "Legislação e regulamentação (LGPD, PCI DSS)",
      "Gestão de riscos e ativos de informação",
      "Liderança estratégica em segurança",
    ],
  },
];

// Card individual de curso
function CourseCard({ course }) {
  return (
    <MKBox
      component="article"
      borderRadius="lg"
      shadow="md"
      p={3}
      mb={3}
      sx={{
        backgroundColor: ({ palette: { white } }) => white.main,
        border: ({ borders: { borderWidth, borderColor } }) =>
          `${borderWidth[1]} solid ${borderColor}`,
        transition: "box-shadow 200ms ease",
        "&:hover": {
          boxShadow: ({ boxShadows: { lg } }) => lg,
        },
      }}
    >
      <Grid container spacing={2} alignItems="flex-start">
        {/* Cabeçalho */}
        <Grid item xs={12}>
          <MKBox display="flex" alignItems="center" flexWrap="wrap" gap={1} mb={0.5}>
            <MKBadge
              badgeContent={course.badge}
              color="info"
              variant="gradient"
              size="sm"
              container
            />
            <MKBox display="flex" alignItems="center" gap={2} ml="auto">
              <MKBox display="flex" alignItems="center" gap={0.5}>
                <Icon fontSize="small" sx={{ color: "text.secondary" }}>schedule</Icon>
                <MKTypography variant="caption" color="text">{course.duration}</MKTypography>
              </MKBox>
              <MKBox display="flex" alignItems="center" gap={0.5}>
                <Icon fontSize="small" sx={{ color: "text.secondary" }}>group</Icon>
                <MKTypography variant="caption" color="text">{course.audience}</MKTypography>
              </MKBox>
            </MKBox>
          </MKBox>
          <MKTypography variant="h5" mt={1} mb={1}>
            {course.title}
          </MKTypography>
          <MKTypography variant="body2" color="text" mb={2}>
            {course.description}
          </MKTypography>
        </Grid>

        {/* Destaques */}
        <Grid item xs={12} md={8}>
          <MKTypography variant="caption" fontWeight="bold" textTransform="uppercase" color="text" mb={1} display="block">
            O que você vai aprender
          </MKTypography>
          <Grid container spacing={1}>
            {course.highlights.map((item, i) => (
              <Grid item xs={12} sm={6} key={i}>
                <MKBox display="flex" alignItems="center" gap={1}>
                  <Icon fontSize="small" color="info">check_circle_outline</Icon>
                  <MKTypography variant="body2" color="text">{item}</MKTypography>
                </MKBox>
              </Grid>
            ))}
          </Grid>
        </Grid>

        {/* CTA */}
        <Grid item xs={12} md={4} display="flex" alignItems="center" justifyContent={{ xs: "flex-start", md: "flex-end" }}>
          <MKButton
            variant="gradient"
            color="info"
            component="a"
            href="/pages/ajuda/contate-nos"
            size="small"
          >
            Solicitar acesso
          </MKButton>
        </Grid>
      </Grid>
    </MKBox>
  );
}

CourseCard.propTypes = {
  course: PropTypes.shape({
    title: PropTypes.string.isRequired,
    badge: PropTypes.string.isRequired,
    duration: PropTypes.string.isRequired,
    audience: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    highlights: PropTypes.arrayOf(PropTypes.string).isRequired,
  }).isRequired,
};

function CourseList() {
  return (
    <MKBox component="section" py={{ xs: 4, md: 8 }}>
      <Container>

        {/* Título da seção */}
        <Grid container item xs={12} lg={8} mb={4}>
          <MKTypography variant="h3">
            Cursos disponíveis
          </MKTypography>
          <MKTypography variant="body2" color="text" mt={1}>
            Todos os cursos incluem avaliação integrada e certificado de conclusão personalizado.
            O catálogo pode ser adaptado ao seu setor, idioma e requisitos regulatórios — entre em
            contato para uma proposta sob medida.
          </MKTypography>
        </Grid>

        {/* Cards de cursos */}
        {elearningCourses.map((course, index) => (
          <CourseCard key={index} course={course} />
        ))}

        <Divider sx={{ my: 4 }} />

        {/* Nota sobre cursos setoriais de saúde */}
        <MKBox
          borderRadius="lg"
          p={3}
          sx={{
            backgroundColor: ({ palette: { info } }) => `${info.main}15`,
            border: ({ borders: { borderWidth }, palette: { info } }) =>
              `${borderWidth[1]} solid ${info.main}40`,
          }}
        >
          <MKBox display="flex" alignItems="flex-start" gap={2}>
            <Icon color="info" sx={{ mt: 0.3 }}>local_hospital</Icon>
            <MKBox>
              <MKTypography variant="h6" mb={0.5}>
                Cursos especializados para o setor de saúde
              </MKTypography>
              <MKTypography variant="body2" color="text">
                A FMP Soluções oferece uma linha completa de cursos de e-Learning sob medida para
                hospitais, clínicas e operadoras de saúde — cobrindo proteção de dados de pacientes,
                conformidade regulatória e resposta a incidentes em ambientes críticos.
                Entre em contato para conhecer o catálogo completo.
              </MKTypography>
              <MKButton
                variant="text"
                color="info"
                size="small"
                component="a"
                href="/pages/ajuda/contate-nos"
                sx={{ mt: 1, pl: 0 }}
              >
                Fale com um especialista →
              </MKButton>
            </MKBox>
          </MKBox>
        </MKBox>

      </Container>
    </MKBox>
  );
}

export default CourseList;
